import { z } from 'zod';
import { jsonByteLength } from './attempt.ts';

/**
 * The allowed blog document (F7 D-4, AC-3.3, T-3.3): the Tiptap JSON the admin editor writes, as an
 * allowlist. `@dcs/rich-text/schema` re-exports it; it lives here because the Worker (which may import
 * `contracts` only, plan §2.2) validates `bodyJson` on `POST`/`PUT /api/admin/posts` with it, so a
 * document with an unknown node, mark or a bad attribute is a 422 and never stored.
 *
 * Nodes: doc, paragraph, heading (levels 2-3), bulletList, orderedList, listItem, blockquote,
 * hardBreak, horizontalRule, image (a blog media path, alt required), questionRef `{ id: 'q015' }`,
 * exerciseRef `{ n: 1..11 }`, text with the marks bold, italic, code and link (`href` http(s) or an
 * internal path). Each node sits where it may (inline nodes in paragraphs and headings, list items in
 * lists only). Attributes the editor adds and the renderer computes (`target`, `rel`, `class`, an
 * image's `title`/`width`/`height`, an ordered list's `type`) are dropped by parsing, not refused.
 *
 * `@dcs/rich-text/render` repeats the href / image / id checks without zod (the site must not ship a
 * validator); packages/rich-text/test/render.test.ts keeps both sides in step.
 */

/** UTF-8 bytes of the serialised document (same as `POST_BODY_MAX_BYTES` and the DB CHECK). */
export const RICH_TEXT_MAX_BYTES = 204_800;
/** Nesting of nodes, the doc being 1 (`doc > bulletList > listItem > paragraph > text` is 5). */
export const RICH_TEXT_MAX_DEPTH = 16;
/** Nodes in one document, text nodes included. */
export const RICH_TEXT_MAX_NODES = 20_000;
export const RICH_TEXT_HREF_MAX = 2048;
export const RICH_TEXT_ALT_MAX = 300;
/** Heading levels the editor offers: the post title is the page's h1. */
export const RICH_TEXT_HEADING_LEVELS = [2, 3] as const;
/** Numbers of the practical exercises (`/sa-hinh/bai-N`). */
export const RICH_TEXT_EXERCISE_MAX = 11;
export const RICH_TEXT_LIST_START_MAX = 10_000;

export const RICH_TEXT_NODE_TYPES = [
  'doc',
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'hardBreak',
  'horizontalRule',
  'image',
  'questionRef',
  'exerciseRef',
  'text',
] as const;
export const RICH_TEXT_MARK_TYPES = ['bold', 'italic', 'code', 'link'] as const;

/** Question ids of the bank (`q001`..`q600`). */
export const QUESTION_REF_PATTERN = /^q\d{3}$/;

/**
 * An image of the blog: `/api/media/<key>` (what the upload answers), or the same path on an API
 * origin. The key is the upload's (`MEDIA_KEY_PATTERN` of post.ts); the renderer re-bases the path on
 * the media origin it is given, so a stored origin never decides where the image loads from.
 */
export const RICH_TEXT_IMAGE_SRC_PATTERN =
  /^(?:https?:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?)?\/api\/media\/(blog\/\d{4}\/(?:0[1-9]|1[0-2])\/[0-9a-f]{32}\.(?:webp|jpg|png|avif))$/;

// Whitespace, control characters and backslashes: never in a stored href (browsers strip or fold
// them, which is how `java\tscript:` and `/\evil.example` sneak through naive checks).
const HREF_FORBIDDEN =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0020\u007f-\u009f\u00a0\u1680\u2000-\u200f\u2028-\u202f\u205f\u3000\ufeff\\]/;
const HREF_ABSOLUTE = /^https?:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:[/?#]|$)/i;

/** A link target: `http(s)://host...` or a path on the site (`/...`, not `//host`). */
export function isSafeHref(href: string): boolean {
  if (href.length === 0 || href.length > RICH_TEXT_HREF_MAX || HREF_FORBIDDEN.test(href)) {
    return false;
  }
  if (href.startsWith('/')) return !href.startsWith('//');
  return HREF_ABSOLUTE.test(href);
}

// ---- Types -----------------------------------------------------------------------------------------

export type RichTextMark =
  | { type: 'bold' }
  | { type: 'italic' }
  | { type: 'code' }
  | { type: 'link'; attrs: { href: string } };

export type RichTextText = {
  type: 'text';
  text: string;
  marks?: RichTextMark[] | undefined;
};
export type RichTextHardBreak = {
  type: 'hardBreak';
  marks?: RichTextMark[] | undefined;
};
export type RichTextQuestionRef = {
  type: 'questionRef';
  attrs: { id: string };
  marks?: RichTextMark[] | undefined;
};
export type RichTextExerciseRef = {
  type: 'exerciseRef';
  attrs: { n: number };
  marks?: RichTextMark[] | undefined;
};
export type RichTextInline =
  RichTextText | RichTextHardBreak | RichTextQuestionRef | RichTextExerciseRef;

export type RichTextParagraph = {
  type: 'paragraph';
  content?: RichTextInline[] | undefined;
};
export type RichTextHeading = {
  type: 'heading';
  attrs: { level: 2 | 3 };
  content?: RichTextInline[] | undefined;
};
export type RichTextListItem = {
  type: 'listItem';
  content: RichTextBlock[];
};
export type RichTextBulletList = {
  type: 'bulletList';
  content: RichTextListItem[];
};
export type RichTextOrderedList = {
  type: 'orderedList';
  attrs?: { start?: number | undefined } | undefined;
  content: RichTextListItem[];
};
export type RichTextBlockquote = {
  type: 'blockquote';
  content: RichTextBlock[];
};
export type RichTextHorizontalRule = {
  type: 'horizontalRule';
};
export type RichTextImage = {
  type: 'image';
  attrs: { src: string; alt: string };
};
export type RichTextBlock =
  | RichTextParagraph
  | RichTextHeading
  | RichTextBulletList
  | RichTextOrderedList
  | RichTextBlockquote
  | RichTextHorizontalRule
  | RichTextImage;

export type RichTextDoc = {
  type: 'doc';
  content?: RichTextBlock[] | undefined;
};

// ---- Schema ----------------------------------------------------------------------------------------

const MarkSchema: z.ZodType<RichTextMark> = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('bold') }),
  z.strictObject({ type: z.literal('italic') }),
  z.strictObject({ type: z.literal('code') }),
  z.strictObject({
    type: z.literal('link'),
    // `target`, `rel`, `class`, `title` of Tiptap's link are dropped: the renderer computes them.
    attrs: z.object({
      href: z.string().refine(isSafeHref, 'Links must be http(s) or a path on the site'),
    }),
  }),
]);

const MarksSchema = z
  .array(MarkSchema)
  .max(RICH_TEXT_MARK_TYPES.length)
  .refine((marks) => new Set(marks.map((m) => m.type)).size === marks.length, 'Repeated mark')
  .optional();

const InlineSchema: z.ZodType<RichTextInline> = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('text'), text: z.string().min(1), marks: MarksSchema }),
  z.strictObject({ type: z.literal('hardBreak'), marks: MarksSchema }),
  z.strictObject({
    type: z.literal('questionRef'),
    attrs: z.object({ id: z.string().regex(QUESTION_REF_PATTERN, 'Not a question id') }),
    marks: MarksSchema,
  }),
  z.strictObject({
    type: z.literal('exerciseRef'),
    attrs: z.object({ n: z.number().int().min(1).max(RICH_TEXT_EXERCISE_MAX) }),
    marks: MarksSchema,
  }),
]);

const inlineContent = z.array(InlineSchema).optional();
const blocks = () => z.array(z.lazy(() => BlockSchema));

const ListItemSchema: z.ZodType<RichTextListItem> = z.strictObject({
  type: z.literal('listItem'),
  content: blocks().min(1),
});

const BlockSchema: z.ZodType<RichTextBlock> = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('paragraph'), content: inlineContent }),
  z.strictObject({
    type: z.literal('heading'),
    attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }),
    content: inlineContent,
  }),
  z.strictObject({ type: z.literal('bulletList'), content: z.array(ListItemSchema).min(1) }),
  z.strictObject({
    type: z.literal('orderedList'),
    // Tiptap's `type: null` is dropped.
    attrs: z
      .object({ start: z.number().int().min(0).max(RICH_TEXT_LIST_START_MAX).optional() })
      .optional(),
    content: z.array(ListItemSchema).min(1),
  }),
  z.strictObject({ type: z.literal('blockquote'), content: blocks().min(1) }),
  z.strictObject({ type: z.literal('horizontalRule') }),
  z.strictObject({
    type: z.literal('image'),
    attrs: z.object({
      src: z.string().regex(RICH_TEXT_IMAGE_SRC_PATTERN, 'Images must be blog media'),
      alt: z.string().trim().min(1, 'Images need a description (alt)').max(RICH_TEXT_ALT_MAX),
    }),
  }),
]);

const NODE_KEYS = new Set(['type', 'attrs', 'content', 'marks', 'text']);
const MARK_KEYS = new Set(['type', 'attrs']);
/** Marks one node may carry before the walk stops looking (the schema allows 4, one of each). */
const MARKS_SCAN_MAX = 16;

type JsonPath = (string | number)[];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isScalar = (value: unknown): boolean =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

export interface RichTextShape {
  /** Deepest node (the doc is 1). */
  readonly depth: number;
  readonly nodes: number;
  /** The first thing that stops the document from being checked further, with its path. */
  readonly problem?: { readonly path: JsonPath; readonly message: string } | undefined;
}

/** Why `attrs` (of a node or a mark) is not a flat object of scalars, if it is not. */
function attrsProblem(attrs: unknown, path: JsonPath): RichTextShape['problem'] {
  if (attrs === undefined) return undefined;
  if (!isPlainObject(attrs)) return { path, message: 'attrs must be an object' };
  for (const [key, item] of Object.entries(attrs)) {
    if (!isScalar(item)) {
      return { path: [...path, key], message: 'Attributes are text, numbers, booleans or null' };
    }
  }
  return undefined;
}

/** Why one node object cannot be checked further (its own keys, attrs and marks), if it cannot. */
function nodeProblem(node: Record<string, unknown>, path: JsonPath): RichTextShape['problem'] {
  for (const key of Object.keys(node)) {
    if (!NODE_KEYS.has(key)) return { path: [...path, key], message: `Unknown key "${key}"` };
  }
  for (const key of ['type', 'text']) {
    if (key in node && !isScalar(node[key])) {
      return { path: [...path, key], message: `${key} must be text` };
    }
  }
  const attrs = attrsProblem(node['attrs'], [...path, 'attrs']);
  if (attrs) return attrs;
  const marks = node['marks'];
  if (marks !== undefined) {
    if (!Array.isArray(marks) || marks.length > MARKS_SCAN_MAX) {
      return { path: [...path, 'marks'], message: 'marks must be a short list' };
    }
    for (const [i, mark] of (marks as unknown[]).entries()) {
      const at = [...path, 'marks', i];
      if (!isPlainObject(mark)) return { path: at, message: 'A mark must be an object' };
      for (const key of Object.keys(mark)) {
        if (!MARK_KEYS.has(key)) return { path: [...at, key], message: `Unknown key "${key}"` };
      }
      if (!isScalar(mark['type'])) return { path: [...at, 'type'], message: 'type must be text' };
      const markAttrs = attrsProblem(mark['attrs'], [...at, 'attrs']);
      if (markAttrs) return markAttrs;
    }
  }
  const content = node['content'];
  if (content !== undefined && !Array.isArray(content)) {
    return { path: [...path, 'content'], message: 'content must be a list' };
  }
  return undefined;
}

/**
 * Walks a parsed JSON value without recursion, so nothing (not even `JSON.stringify` afterwards)
 * can overflow the stack. Nesting is possible through `content` only: a node holds the keys of a
 * node only, `type`/`text` are scalars, `attrs` (of nodes and marks) are flat objects of scalars and
 * `marks` a short list of flat marks. Stops at the first problem, or past the depth or node limit.
 */
export function richTextShape(value: unknown): RichTextShape {
  let depth = 0;
  let nodes = 0;
  const stack: [unknown, number, JsonPath][] = [[value, 1, []]];
  while (stack.length > 0) {
    const [node, level, path] = stack.pop() as [unknown, number, JsonPath];
    nodes += 1;
    if (level > depth) depth = level;
    if (depth > RICH_TEXT_MAX_DEPTH || nodes > RICH_TEXT_MAX_NODES) break;
    if (!isPlainObject(node)) {
      // A scalar or a wrong root is the schema's to report; a list in a list is nesting.
      if (Array.isArray(node) && level > 1) {
        return { depth, nodes, problem: { path, message: 'A node must be an object' } };
      }
      continue;
    }
    const problem = nodeProblem(node, path);
    if (problem) return { depth, nodes, problem };
    const content = node['content'];
    if (Array.isArray(content)) {
      (content as unknown[]).forEach((child, i) => {
        stack.push([child, level + 1, [...path, 'content', i]]);
      });
    }
  }
  return { depth, nodes };
}

/** Bytes of `value` as stored; `Infinity` when it cannot be serialised (never expected after the walk). */
function storedBytes(value: unknown): number {
  try {
    return jsonByteLength(value);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * The allowed document. The shape walk comes first (iterative: unknown keys, nesting outside
 * `content`, depth and node count), then the size (safe to serialise by then, and guarded anyway);
 * only a document within all of them is checked node by node.
 */
export const RichTextDocSchema: z.ZodType<RichTextDoc> = z
  .unknown()
  .superRefine((value, ctx) => {
    const { depth, nodes, problem } = richTextShape(value);
    if (depth > RICH_TEXT_MAX_DEPTH) {
      ctx.addIssue({
        code: 'custom',
        message: `Nodes nest at most ${String(RICH_TEXT_MAX_DEPTH)} deep`,
      });
    } else if (nodes > RICH_TEXT_MAX_NODES) {
      ctx.addIssue({ code: 'custom', message: `At most ${String(RICH_TEXT_MAX_NODES)} nodes` });
    } else if (problem) {
      ctx.addIssue({ code: 'custom', message: problem.message, path: problem.path });
    } else if (storedBytes(value) > RICH_TEXT_MAX_BYTES) {
      ctx.addIssue({
        code: 'custom',
        message: `At most ${String(RICH_TEXT_MAX_BYTES)} bytes once serialised`,
      });
    }
  })
  .pipe(z.strictObject({ type: z.literal('doc'), content: blocks().optional() }));
